/**
 * CrownMeta — list of inline metric numbers shown next to the page heading.
 * Each item: mono 10 px label above an Inter 26 px number, with optional
 * subscript suffix (e.g. "61 / 96").
 *
 * Source: .agents/design/project/artboard-radical.jsx:124-137
 */

import type { ReactNode } from 'react';

export interface CrownMetaItem {
  label: ReactNode;
  /** Number / string to render large. */
  value: ReactNode;
  /** Optional small grey suffix appended after the value. */
  suffix?: ReactNode;
}

interface CrownMetaProps {
  items: CrownMetaItem[];
  className?: string;
  /**
   * Phone (max-sm) grid columns. Default 2 (2×2 for four stats). Pass 4 only
   * when every value is a short numeric ("64", "8/11", "39 %") so all four
   * stats hold a single row — long values (names, money, dates) need 2.
   */
  phoneCols?: 2 | 4;
}

export function CrownMeta({ items, className = '', phoneCols = 2 }: CrownMetaProps) {
  return (
    <dl
      className={
        // Phone (14.5): the inline stat row becomes a 2×2 grid (handoff
        // PhoneDashboard) — four 26px numbers never fit on one 390px line.
        // Short numeric crowns opt into a single 4-up row via phoneCols.
        // Tablet (widened after iPad-portrait testing): the inspector-
        // squeezed center column clipped the row — stats wrap instead.
        'flex items-end gap-7 max-lg:flex-wrap max-lg:gap-x-6 max-lg:gap-y-2.5 max-sm:grid ' +
        (phoneCols === 4
          ? 'max-sm:grid-cols-4 max-sm:gap-2 '
          : 'max-sm:grid-cols-2 max-sm:gap-3 ') +
        className
      }
    >
      {items.map((item, i) => (
        // min-w-0 lets the grid item shrink below its content so the value's
        // truncate can engage — grid items default to min-width:auto, which
        // would let a long firma/customer name blow the 2×2 phone grid open.
        <div key={i} className="max-sm:min-w-0">
          <dt className="mb-1 font-mono text-[10px] uppercase tracking-[0.08em] text-ink-3">
            {item.label}
          </dt>
          <dd className="font-sans text-[26px] font-bold leading-none tracking-[-0.02em] text-ink max-sm:truncate max-sm:text-[20px]">
            {item.value}
            {item.suffix != null && (
              <small className="ml-0.5 text-[13px] font-medium text-ink-3">
                {item.suffix}
              </small>
            )}
          </dd>
        </div>
      ))}
    </dl>
  );
}
